import React, { useState } from "react";
import { Button, Card, Icon } from "@nelurio/ui";
import { useI18n } from "@nelurio/i18n";
import { api } from "../api/client";

interface AiCoachResponse {
  text: string;
  provider: string;
  model: string;
}

/**
 * One AI coaching surface ("the Atria blood"): a card that generates a short
 * coach text from live account data. Self-contained — owns its own state so
 * embedding pages keep their hook order untouched.
 * kind "brief": auto-generates once per day (cached in localStorage).
 * kind "weekly": manual only.
 */
export function AiCoachCard({ kind }: { kind: "brief" | "weekly" }) {
  const { t, locale } = useI18n();
  const [text, setText] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const cacheKey = `nelurio.ai.${kind}.v1`;
  const todayKey = `${new Date().toLocaleDateString("sv")}|${locale}`;

  React.useEffect(() => {
    if (kind !== "brief") return;
    try {
      const cached = window.localStorage.getItem(cacheKey);
      if (cached && cached.startsWith(`${todayKey}|`)) {
        setText(cached.slice(todayKey.length + 1));
      }
    } catch { /* private mode */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const generate = async () => {
    setLoading(true);
    setError(false);
    try {
      const endpoint = kind === "brief" ? "/ai/brief" : "/ai/weekly";
      // The live UI locale decides the language of the generated text.
      const response = await api<AiCoachResponse>(endpoint, { method: "POST", body: JSON.stringify({ locale }) });
      setText(response.text);
      if (kind === "brief") {
        try { window.localStorage.setItem(cacheKey, `${todayKey}|${response.text}`); } catch { /* private mode */ }
      }
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  const title = kind === "brief" ? t("ai.briefTitle") : t("ai.weeklyTitle");

  return (
    <Card className="ai-coach">
      <div className="ai-coach__head">
        <span className="ai-coach__orb" aria-hidden="true"><Icon name="sparkle" /></span>
        <div className="ai-coach__titlebox">
          <span className="section-kicker">{t("ai.kicker")}</span>
          <h2>{title}</h2>
        </div>
        {text !== null && !loading && (
          <Button variant="secondary" icon="sparkle" onClick={() => void generate()}>{t("ai.refresh")}</Button>
        )}
      </div>
      {loading ? (
        <p className="ai-coach__text ai-coach__text--loading">{t("ai.thinking")}</p>
      ) : text !== null ? (
        <>
          <p className="ai-coach__text">{text}</p>
          <small className="ai-coach__note">{t("ai.generatedNotice")}</small>
        </>
      ) : error ? (
        <p className="ai-coach__text ai-coach__text--error">{t("ai.error")}</p>
      ) : (
        <div className="ai-coach__idle">
          <p className="ai-coach__hint">{t("ai.briefHint")}</p>
          <Button icon="sparkle" className="ai-coach__cta" onClick={() => void generate()}>{t("ai.generate")}</Button>
        </div>
      )}
    </Card>
  );
}
